import { queryOptions, useQueryClient, useSuspenseQuery, type QueryClient } from '@tanstack/react-query';
import type { ReportingWebhook as UIAPIReportingWebhook } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import type { QueryConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import { parseWebhook, webhookKeys, type Webhook } from './entity';

/**
 * Reporting-webhook reads. Same bulletproof shape as the rest of the layer:
 * a plain async fetcher, a `getXQueryOptions` factory, and a thin suspense hook.
 *
 * Both reads use `staleTime: 0`. The Ember list route re-`findAll`s on every
 * visit (it drives create/enable validation), and the edit route always wants
 * the current row — so neither should serve a stale cache entry.
 */

const STALE_TIME = 0;

/** The single-webhook response carries an optional `webhook`; assert it. */
function requireWebhook(res: { webhook?: UIAPIReportingWebhook }): UIAPIReportingWebhook {
  if (res.webhook == null) {
    throw new Error('reporting webhook response did not include a webhook');
  }
  return res.webhook;
}

/** @public */
export async function getWebhooks(workspaceId: number, qc: QueryClient): Promise<Webhook[]> {
  const data = await hydraUiApi.reportingWebhooks.getWebhooks({
    params: { path: { environment_id: String(workspaceId) } },
  });
  const parsed = (data.webhooks ?? []).map(parseWebhook);
  // Sideload each row into its detail slot so a follow-up edit read resolves
  // from cache without an extra GET.
  for (const w of parsed) {
    qc.setQueryData(webhookKeys.detail(workspaceId, w.id), w);
  }
  return parsed;
}

/** @public */
export const getWebhooksQueryOptions = (workspaceId: number, qc: QueryClient) =>
  queryOptions({
    // `TListParams = void` for this entity — the no-filters list slot.
    queryKey: webhookKeys.list(workspaceId, undefined),
    queryFn: () => getWebhooks(workspaceId, qc),
    staleTime: STALE_TIME,
  });

interface UseWebhooksOptions {
  queryConfig?: QueryConfig<typeof getWebhooksQueryOptions>;
}

/** @public */
export const useWebhooks = ({ queryConfig }: UseWebhooksOptions = {}) => {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  return useSuspenseQuery({
    ...getWebhooksQueryOptions(workspaceId, qc),
    ...queryConfig,
  });
};

/** @public */
export async function getWebhook(workspaceId: number, webhookId: string): Promise<Webhook> {
  const data = await hydraUiApi.reportingWebhooks.getWebhooks2({
    params: { path: { environment_id: String(workspaceId), id: webhookId } },
  });
  return parseWebhook(requireWebhook(data));
}

/** @public */
export const getWebhookQueryOptions = (workspaceId: number, webhookId: string) =>
  queryOptions({
    queryKey: webhookKeys.detail(workspaceId, webhookId),
    queryFn: () => getWebhook(workspaceId, webhookId),
    staleTime: STALE_TIME,
  });

interface UseWebhookOptions {
  id: string;
  queryConfig?: QueryConfig<typeof getWebhookQueryOptions>;
}

/** @public */
export const useWebhook = ({ id, queryConfig }: UseWebhookOptions) => {
  const workspaceId = requireWorkspaceId();
  return useSuspenseQuery({
    ...getWebhookQueryOptions(workspaceId, id),
    ...queryConfig,
  });
};
